"use client";

import { motion, useReducedMotion } from "motion/react";
import { Link } from "@/i18n/navigation";
import type { CaseStudyKind } from "@/config/case-studies";
import { IconArrowForward } from "@/components/icons";
import { CaseStudyKindIcon } from "./case-study-icons";

const labelClass =
  "text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-semibold text-[var(--primary)]";

const paragraphClass =
  "text-[var(--on-surface-variant)] leading-relaxed text-[1rem] sm:text-[1.0625rem] max-w-[40rem]";

const easeOutQuart = [0.25, 1, 0.5, 1] as const;

export type CaseStudyIndexRowData = {
  slug: string;
  index: number;
  kind: CaseStudyKind;
  tagline: string;
  title: string;
  teaser: string;
};

export function CaseStudiesIndexListClient({ rows }: { rows: CaseStudyIndexRowData[] }) {
  return (
    <ul className="grid grid-cols-12 gap-y-10 sm:gap-y-14 md:gap-y-16 lg:gap-x-10 list-none p-0 m-0">
      {rows.map((row) => (
        <CaseStudyIndexRowMotion key={row.slug} row={row} />
      ))}
    </ul>
  );
}

function CaseStudyIndexRowMotion({ row }: { row: CaseStudyIndexRowData }) {
  const { slug, index, kind, tagline, title, teaser } = row;
  const reduceMotion = useReducedMotion();
  const isOdd = index % 2 === 0;
  const colClass = isOdd
    ? "col-span-12 lg:col-span-7 xl:col-span-6 lg:col-start-1"
    : "col-span-12 lg:col-span-7 xl:col-span-6 lg:col-start-6 xl:col-start-7";

  const delay = reduceMotion ? 0 : index * 0.07;

  return (
    <motion.li
      className={colClass}
      initial={reduceMotion ? undefined : { opacity: 0, y: 20 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px 0px" }}
      transition={{
        duration: reduceMotion ? 0 : 0.48,
        delay,
        ease: easeOutQuart,
      }}
    >
      <Link
        href={`/case-studies/${slug}`}
        className="group block rounded-[var(--radius-sm)] bg-[var(--surface-container-low)] px-5 py-6 sm:px-7 sm:py-8 md:px-8 md:py-10 transition-[background-color,box-shadow] duration-300 ease-[var(--ease-out-quint)] hover:bg-[var(--surface-container)] motion-safe:hover:shadow-[0_0_36px_rgba(225,225,247,0.07)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
      >
        <span
          className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] bg-[color-mix(in_srgb,var(--primary-container)_95%,transparent)] text-[var(--primary)] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_20%,transparent)] motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[var(--ease-out-quint)] motion-safe:group-hover:scale-[1.04]"
          aria-hidden
        >
          <CaseStudyKindIcon kind={kind} className="h-[22px] w-[22px]" />
        </span>
        <span
          className={`${labelClass} mb-4 block text-[color-mix(in_srgb,var(--primary)_88%,var(--on-surface)_12%)]`}
          style={{ fontFamily: "var(--font-body)" }}
        >
          {tagline}
        </span>
        <h2
          className="font-display text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.5vw,2.25rem)] text-[var(--on-surface)] mb-4 tracking-tight leading-tight group-hover:underline decoration-[color-mix(in_srgb,var(--primary)_70%,transparent)] underline-offset-[0.35rem] motion-safe:transition-[text-decoration-color] motion-safe:duration-200"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {title}
        </h2>
        <p
          className={`${paragraphClass} max-w-none lg:max-w-[34rem]`}
          style={{ fontFamily: "var(--font-body)" }}
        >
          {teaser}
        </p>
        <span
          className="mt-6 inline-flex items-center gap-2 text-[var(--label-md)] uppercase tracking-[0.12em] font-semibold text-[var(--primary)] motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[var(--ease-out-quint)] motion-safe:group-hover:translate-x-1"
          style={{ fontFamily: "var(--font-body)" }}
        >
          <IconArrowForward className="h-4 w-4 text-[var(--primary)]" />
        </span>
      </Link>
    </motion.li>
  );
}
